import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Auto-discover all locale JSON files in the locales folder.
// Adding a new file (e.g. locales/fr.json) automatically registers it.
const localeModules = import.meta.glob<Record<string, unknown>>(
  '../locales/*.json',
  { eager: true, import: 'default' },
);

const resources: Record<string, { translation: Record<string, unknown> }> = {};

for (const [path, mod] of Object.entries(localeModules)) {
  const code = path.match(/\/(\w+)\.json$/)?.[1];
  if (code) {
    resources[code] = { translation: mod };
  }
}

/** Sorted list of available language codes, derived from the locales folder. */
export const availableLanguages = Object.keys(resources).sort();

/** Locale codes that use right-to-left script. */
const RTL_LOCALES = new Set(['ar', 'he', 'fa', 'ur']);

/** Returns true if the given locale code uses right-to-left script. */
export function isRtlLocale(code: string): boolean {
  return RTL_LOCALES.has(code);
}

/** Returns `"rtl"` or `"ltr"` for the given locale code. */
export function getLocaleDirection(code: string): 'rtl' | 'ltr' {
  return isRtlLocale(code) ? 'rtl' : 'ltr';
}

/** Get the native display name for a locale code (e.g. "fr" → "Français"). */
export function getLanguageDisplayName(code: string): string {
  try {
    const dn = new Intl.DisplayNames([code], { type: 'language' });
    const name = dn.of(code);
    if (name) return name.charAt(0).toUpperCase() + name.slice(1);
  } catch {
    // fallback
  }
  return code.toUpperCase();
}

const STORAGE_KEY = 'quicktl-lang';
const stored = localStorage.getItem(STORAGE_KEY);
const initialLng = stored && stored in resources ? stored : 'en';

i18n.use(initReactI18next).init({
  resources,
  lng: initialLng,
  fallbackLng: 'en',
  returnObjects: true,
  interpolation: {
    escapeValue: false,
  },
});

i18n.on('languageChanged', (lng) => {
  localStorage.setItem(STORAGE_KEY, lng);
  document.documentElement.dir = getLocaleDirection(lng);
  document.documentElement.lang = lng;
});

// Set initial direction and lang on the document
document.documentElement.dir = getLocaleDirection(initialLng);
document.documentElement.lang = initialLng;

export default i18n;
